import { defineMessages } from 'react-intl';

import {
  ServiceGroupName,
  ServiceType,
} from 'protocol/api/billing_new/dto_services_new_pb';
import {
  FeatureFlag,
  OrganizationType,
} from 'protocol/model/dto_organization_pb';

export const modal = defineMessages({
  yes: {
    id: 'modal.yes',
    defaultMessage: 'Yes',
  },
  no: {
    id: 'modal.no',
    defaultMessage: 'No',
  },
});

export const lifeTimeMonthDefaultMessages = defineMessages<number>({
  0: {
    id: 'lifetime.month.0',
    defaultMessage: 'Lifetime',
  },
  1: {
    id: 'lifetime.month.1',
    defaultMessage: 'Monthly',
  },
  3: {
    id: 'lifetime.month.3',
    defaultMessage: '3 months',
  },
  4: {
    id: 'lifetime.month.4',
    defaultMessage: '4 months',
  },
  6: {
    id: 'lifetime.month.6',
    defaultMessage: '6 months',
  },
  12: {
    id: 'lifetime.month.12',
    defaultMessage: 'Annual',
  },
});

export const ServiceNamesDefaultMessage = defineMessages<ServiceType>({
  [ServiceType.ServiceTypeInvalidValue]: {
    id: 'subscriptions.service.0',
    defaultMessage: 'Invalid Value',
  },
  // WA-6209: uploads are no longer metered, so the per-upload counters (1..6)
  // were removed from the protocol. Dental-photo upload survives as the enabler.
  [ServiceType.ServiceType_Enable_UploadDentalPhoto]: {
    id: 'subscriptions.service.5',
    defaultMessage: 'Dental Photo Uploads',
  },
  // Both countable and enabler
  [ServiceType.ServiceType_Order_CBCT_GP_Analysis]: {
    id: 'subscriptions.service.100',
    defaultMessage:
      '{amount, plural, =0 {Radiology Report} one {{amount} Radiology Report} other {{amount} Radiology Reports}}',
  },
  // Both countable and enabler
  [ServiceType.ServiceType_Order_CBCT_Endodontic_Analysis]: {
    id: 'subscriptions.service.101',
    defaultMessage:
      '{amount, plural, =0 {Endo Report} one {{amount} Endo Report} other {{amount} Endo Reports}}',
  },
  // Both countable and enabler
  [ServiceType.ServiceType_Order_CBCT_Implantology_Analysis]: {
    id: 'subscriptions.service.102',
    defaultMessage:
      '{amount, plural, =0 {Implant Report} one {{amount} Implant Report} other {{amount} Implant Reports}}',
  },
  // Both countable and enabler
  [ServiceType.ServiceType_Order_CBCT_ThirdMolar_Analysis]: {
    id: 'subscriptions.service.103',
    defaultMessage:
      '{amount, plural, =0 {3rd Molar Report} one {{amount} 3rd Molar Report} other {{amount} 3rd Molar Reports}}',
  },
  // Both countable and enabler
  [ServiceType.ServiceType_Order_CBCT_STL_Analysis]: {
    id: 'subscriptions.service.104',
    defaultMessage:
      '{amount, plural, =0 {DICOM to STL conversion} one {{amount} DICOM to STL conversion} other {{amount} DICOM to STL conversions}}',
  },
  // Both countable and enabler
  [ServiceType.ServiceType_Order_CBCT_Orthodontic_Analysis]: {
    id: 'subscriptions.service.105',
    defaultMessage:
      '{amount, plural, =0 {Ortho Report} one {{amount} Ortho Report} other {{amount} Ortho Reports}}',
  },
  // Both countable and enabler
  [ServiceType.ServiceType_Order_CBCT_IOS_Superimposition_Analysis]: {
    id: 'subscriptions.service.106',
    defaultMessage:
      '{amount, plural, =0 {CBCT IOS Superimposition} other {{amount} CBCT IOS Superimposition}}',
  },
  // Both countable and enabler
  [ServiceType.ServiceType_Order_IOS_Segmentation_Analysis]: {
    id: 'subscriptions.service.107',
    defaultMessage:
      '{amount, plural, =0 {IOS Segmentation} one {{amount} IOS Segmentation} other {{amount} IOS Segmentations}}',
  },
  // Both countable and enabler
  [ServiceType.ServiceType_Order_IOXRay_GP_Analysis]: {
    id: 'subscriptions.service.108',
    defaultMessage:
      '{amount, plural, =0 {FMX AI Report} one {{amount} FMX AI Report} other {{amount} FMX AI Reports}}',
  },
  // Both countable and enabler
  [ServiceType.ServiceType_Order_Panoramic_GP_Analysis]: {
    id: 'subscriptions.service.109',
    defaultMessage:
      '{amount, plural, =0 {Pano AI Report} one {{amount} Pano AI Report} other {{amount} Pano AI Reports}}',
  },
  // Both countable and enabler
  [ServiceType.ServiceType_Order_DentalPhoto_GP_Analysis]: {
    id: 'subscriptions.service.110',
    defaultMessage:
      '{amount, plural, =0 {Dental Photo Classification} one {{amount} Dental Photo Classification} other {{amount} Dental Photo Classification}}',
  },
  [ServiceType.ServiceType_Order_ImplantStudio_Analysis]: {
    id: 'subscriptions.service.111',
    defaultMessage: 'Implant studio analysis',
  },
  [ServiceType.ServiceType_Order_OrthoStudio_Analysis]: {
    id: 'subscriptions.service.112',
    defaultMessage: 'Ortho studio analysis',
  },
  // Only enabler
  [ServiceType.ServiceType_Enable_CollaborationTool]: {
    id: 'subscriptions.service.300',
    defaultMessage: 'Collaboration Tool',
  },
  // Only enabler
  [ServiceType.ServiceType_Enable_DesktopApp]: {
    id: 'subscriptions.service.302',
    defaultMessage: 'Desktop application',
  },
  [ServiceType.ServiceType_Enable_3DModelsWithConditions]: {
    id: 'subscriptions.service.203',
    defaultMessage: '3D models with conditions',
  },
  [ServiceType.ServiceType_Enable_SuggestedTreatment]: {
    id: 'subscriptions.service.204',
    defaultMessage: 'Suggested treatment',
  },
  [ServiceType.ServiceType_Enable_Transcriptor]: {
    id: 'subscriptions.service.205',
    defaultMessage:
      '{amount, plural, =0 {Transcriptor} one {{amount} Transcriptor} other {{amount} Transcriptor}}',
  },
  [ServiceType.ServiceType_Expand_Storage]: {
    id: 'subscriptions.service.206',
    defaultMessage: 'Expand storage',
  },
  [ServiceType.ServiceType_Order_Transcriptor]: {
    id: 'subscriptions.service.113',
    defaultMessage:
      '{amount, plural, =0 {Transcriptor} one {{amount} Transcriptor} other {{amount} Transcriptor}}',
  },
});

export const GroupNamesDefaultMessage = defineMessages<ServiceGroupName>({
  [ServiceGroupName.ServiceGroupNameInvalidValue]: {
    id: 'global.invalidValue',
    defaultMessage: 'Invalid value',
  },
  [ServiceGroupName.ServiceGroupNameEmpty]: {
    id: 'subscriptions.group.1',
    defaultMessage: 'Empty group',
  },
  [ServiceGroupName.ServiceGroupNameAnalysis3D]: {
    id: 'subscriptions.group.2',
    defaultMessage:
      '{amount, plural, =0 {3D Reports} one {3D Reports} other {{amount} 3D Reports}}',
  },
  [ServiceGroupName.ServiceGroupNameAnalysis2D]: {
    id: 'subscriptions.group.3',
    defaultMessage:
      '{amount, plural, =0 {2D Reports} one {2D Reports} other {{amount} 2D Reports}}',
  },
  [ServiceGroupName.ServiceGroupNameUpload3D]: {
    id: 'subscriptions.group.4',
    defaultMessage:
      '{amount, plural, =0 {3D Uploads} one {3D Uploads} other {{amount} 3D Uploads}}',
  },
  [ServiceGroupName.ServiceGroupNameUpload2D]: {
    id: 'subscriptions.group.5',
    defaultMessage:
      '{amount, plural, =0 {2D Uploads} one {2D Uploads} other {{amount} 2D Uploads}}',
  },
  [ServiceGroupName.ServiceGroupNameTrial]: {
    id: 'subscriptions.group.6',
    defaultMessage:
      '{amount, plural, =0 {Trial credits} one {Trial credit} other {{amount} Trial credits}}',
  },
});

export const organizationTypeDefaultMessages = defineMessages<OrganizationType>(
  {
    [OrganizationType.OrganizationType_InvalidValue]: {
      id: 'organizationType.OrganizationType_InvalidValue',
      defaultMessage: 'Invalid value',
    },
    [OrganizationType.OrganizationType_Dental]: {
      id: 'organizationType.OrganizationType_Dental',
      defaultMessage: 'Dental',
    },
    [OrganizationType.OrganizationType_NonDental]: {
      id: 'organizationType.OrganizationType_NonDental',
      defaultMessage: 'Non-dental',
    },
  },
);

export const featureFlagsDefaultMessages = defineMessages<FeatureFlag>({
  [FeatureFlag.FeatureFlag_InvalidValue]: {
    id: 'featureFlag.name.invalidValue',
    defaultMessage: 'Invalid feature flag',
  },
  [FeatureFlag.FeatureFlag_Show_BillingShop]: {
    id: 'featureFlag.name.showBillingShop',
    defaultMessage: 'Show billing shop',
  },
  [FeatureFlag.FeatureFlag_Hide_NonDentalFindings]: {
    id: 'featureFlag.name.hideNonDentalFindings',
    defaultMessage: 'Hide other findings',
  },
  [FeatureFlag.FeatureFlag_Hide_Probabilities]: {
    id: 'featureFlag.name.hideProbabilities',
    defaultMessage: 'Hide probabilities',
  },
  [FeatureFlag.FeatureFlag_Hide_ConditionsMasks]: {
    id: 'featureFlag.name.hideConditionsMasks',
    defaultMessage: 'Hide conditions masks',
  },
  [FeatureFlag.FeatureFlag_Hide_PerioMasksAndSlices]: {
    id: 'featureFlag.name.hidePerioMasks',
    defaultMessage: 'Hide perio masks',
  },
  [FeatureFlag.FeatureFlag_Hide_CariesSigns]: {
    id: 'featureFlag.name.hideCariesSigns',
    defaultMessage: 'Hide caries signs',
  },
  [FeatureFlag.FeatureFlag_Hide_PBLRulesAndMeasurements]: {
    id: 'featureFlag.name.hidePBLRulesAndMeasurements',
    defaultMessage: 'Hide PBL rules and measurements',
  },
  [FeatureFlag.FeatureFlag_Show_SegmentronConditionsButtons]: {
    id: 'featureFlag.name.showSegmentronConditions',
    defaultMessage: 'Segmentron: enable conditions buttons',
  },
  [FeatureFlag.FeatureFlag_Enable_SegmentronSerialisation]: {
    id: 'featureFlag.name.enableSegmentronSerialisation',
    defaultMessage: 'Segmentron: enable serialisation',
  },
  [FeatureFlag.FeatureFlag_Enable_ToothSuggestedTreatment]: {
    id: 'featureFlag.name.enableToothSuggestedTreatment',
    defaultMessage: 'Enable tooth suggested treatment',
  },
  [FeatureFlag.FeatureFlag_FDA_Annotator]: {
    id: 'featureFlag.name.fdaAnnotator',
    defaultMessage: 'FDA Annotator',
  },
  [FeatureFlag.FeatureFlag_FDA_Unaided]: {
    id: 'featureFlag.name.fdaUnaided',
    defaultMessage: 'FDA Unaided',
  },
  [FeatureFlag.FeatureFlag_Show_NotForCommercialUseBanner]: {
    id: 'featureFlag.name.showNotForCommercialUseBanner',
    defaultMessage: 'Show Not For Commercial Use Banner',
  },
  [FeatureFlag.FeatureFlag_Show_IOSReportButton]: {
    id: 'featureFlag.name.showIOSReportPlanningButton',
    defaultMessage: 'Show IOS report planning button',
  },
  [FeatureFlag.FeatureFlag_Show_OrthoStudioAddSecondaryCBCT]: {
    id: 'featureFlag.name.showOrthoStudioAddSecondaryCBCT',
    defaultMessage: 'Ortho Studio: add secondary CBCT',
  },
  [FeatureFlag.FeatureFlag_Hide_AnatomyMasks]: {
    id: 'featureFlag.name.hideAnatomyMasks',
    defaultMessage: 'Hide Anatomy Masks',
  },
  [FeatureFlag.FeatureFlag_Hide_AllCBCTMasks]: {
    id: 'featureFlag.name.hideAllCBCTMasks',
    defaultMessage: 'Hide All CBCT Masks',
  },
  [FeatureFlag.FeatureFlag_Show_DentalPhotosWidget]: {
    id: 'featureFlag.name.showDentalPhotosWidget',
    defaultMessage: 'Show dental photos widget',
  },
  [FeatureFlag.FeatureFlag_Enable_OrthoStudio]: {
    id: 'featureFlag.name.enableOrthoStudio',
    defaultMessage: 'Ortho Studio',
  },
  [FeatureFlag.FeatureFlag_Show_CBCTExtendedConclusion]: {
    id: 'featureFlag.name.showCBCTExtendedConclusion',
    defaultMessage: 'Show CBCT extended conclusion',
  },
  [FeatureFlag.FeatureFlag_Enable_AgentChat]: {
    id: 'featureFlag.name.enableAgentChat',
    defaultMessage: 'Enable Agent chat',
  },
  [FeatureFlag.FeatureFlag_Forbid_UploadsFromWeb]: {
    id: 'featureFlag.name.forbidUploadsFromWeb',
    defaultMessage: 'Forbid uploads from web',
  },
  [FeatureFlag.FeatureFlag_Forbid_PatientEditing]: {
    id: 'featureFlag.name.forbidPatientEditing',
    defaultMessage: 'Forbid patient creation and editing',
  },
  [FeatureFlag.FeatureFlag_Forbid_Sharing]: {
    id: 'featureFlag.name.forbidSharing',
    defaultMessage: 'Forbid sharing',
  },
  [FeatureFlag.FeatureFlag_FDA_SegmentronViewerRestrictions]: {
    id: 'featureFlag.name.fdaSegmentronViewerRestrictions',
    defaultMessage: 'FDA Segmentron Viewer Restrictions',
  },
  [FeatureFlag.FeatureFlag_FDA_3DGPRestrictions]: {
    id: 'featureFlag.name.fda3DGPRestrictions',
    defaultMessage: 'FDA 3D GP Restrictions',
  },
  [FeatureFlag.FeatureFlag_FDA_Version2]: {
    id: 'featureFlag.name.fdaVersion2',
    defaultMessage: 'FDA Version 2',
  },
  [FeatureFlag.FeatureFlag_Enable_StorageFeature]: {
    id: 'featureFlag.name.enableStorageFeature',
    defaultMessage: 'Enable Storage feature',
  },
  [FeatureFlag.FeatureFlag_Enable_FullHeadMPRs]: {
    id: 'featureFlag.name.enableFullHeadMPRs',
    defaultMessage: 'Enable full-head MPRs',
  },
  [FeatureFlag.FeatureFlag_Enable_DICOMToDCCTConversion]: {
    id: 'featureFlag.name.enableDICOMToDCCTConversion',
    defaultMessage: 'Enable DICOM to DCCT conversion',
  },
  [FeatureFlag.FeatureFlag_Enable_DICOMToDCCTConversionFallback]: {
    id: 'featureFlag.name.enableDICOMToDCCTConversionFallback',
    defaultMessage: 'Enable DICOM to DCCT conversion fallback',
  },
  [FeatureFlag.FeatureFlag_Enable_SegmentronAirwaysHeatmap]: {
    id: 'featureFlag.name.enableSegmentronAirwaysHeatmap',
    defaultMessage: 'Enable Segmentron Airways Heatmap',
  },
  [FeatureFlag.FeatureFlag_Enable_ConsultationTemplates]: {
    id: 'featureFlag.name.enableConsultationTemplates',
    defaultMessage: 'Enable consultation templates',
  },
  [FeatureFlag.FeatureFlag_Enable_Analytics]: {
    id: 'featureFlag.name.enableAnalytics',
    defaultMessage: 'Enable analytics',
  },
  [FeatureFlag.FeatureFlag_Enable_Encounters]: {
    id: 'featureFlag.name.enableEncounters',
    defaultMessage: 'Enable encounters',
  },
  [FeatureFlag.FeatureFlag_Enable_CommunityPublishing]: {
    id: 'featureFlag.name.enableCommunityPublishing',
    defaultMessage: 'Enable community publishing',
  },
  [FeatureFlag.FeatureFlag_Enable_Diagnocoins]: {
    id: 'featureFlag.name.enableDiagnocoins',
    defaultMessage: 'Enable Diagnocoins',
  },
  [FeatureFlag.FeatureFlag_Enable_ArchiveReports]: {
    id: 'featureFlag.name.enableArchiveReports',
    defaultMessage: 'Enable report archiving',
  },
  [FeatureFlag.FeatureFlag_Enable_MaskEditing]: {
    id: 'featureFlag.name.enableMaskEditing',
    defaultMessage: 'Enable mask editing',
  },
  [FeatureFlag.FeatureFlag_Enable_ReferralProgram]: {
    id: 'featureFlag.name.enableReferralProgram',
    defaultMessage: 'Enable referral program',
  },
  [FeatureFlag.FeatureFlag_Enable_SegmentronROI]: {
    id: 'featureFlag.name.enableSegmentronROI',
    defaultMessage: 'Enable Segmentron ROI',
  },
  [FeatureFlag.FeatureFlag_Enable_MFASettings]: {
    id: 'featureFlag.name.enableMFASettings',
    defaultMessage: 'Show two-step verification settings',
  },
});
